// FLAGS: none
// Filter bar for the session lists: status, date range, expert, employer and free text.
import { Button, ButtonGroup } from "@mui/material";
import OpenUpButton from "@openup/shared/components/Button/Button";
import {
  ModelsConsultStatus,
  ModelsEmployer,
  ResponsePsychologistResponse,
} from "@src/api/v1/data-contracts";
import SearchBox from "@src/components/SearchBox";
import DateRangeDialog from "@src/components/SessionFilters/DateRangeDialog";
import { STATUS } from "@src/constants";
import { useEmployersQuery } from "@src/queries/employers";
import { Range } from "react-date-range";
import { FC, useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useDebouncedCallback } from "use-debounce";
import { usePsychologistsQuery } from "@src/queries/experts";
import PsychologyOutlined from "@mui/icons-material/PsychologyOutlined";
import WorkOutlineOutlined from "@mui/icons-material/WorkOutlineOutlined";
import DateFilterButton from "@src/components/SessionFilters/DateFilterButton";
import { endOfDay, startOfDay } from "date-fns";
import DropdownFilter from "./DropdownFilter";

export type SessionsFilter = {
  searchTerm: string;
  status?: ModelsConsultStatus;
  startedAtFrom?: Date;
  startedAtTo?: Date;
  employer?: ModelsEmployer;
  expert?: ResponsePsychologistResponse;
};

interface SessionFiltersProps {
  onFilterChanged: (filter: SessionsFilter) => void;
  searchPlaceholder?: string;
  filters: SessionsFilter;
  onFilterCleared: () => void;
  showExpertFilter?: boolean;
}

export const SessionFilters: FC<SessionFiltersProps> = ({
  searchPlaceholder,
  onFilterChanged,
  filters,
  onFilterCleared,
  showExpertFilter = true,
}) => {
  const { t } = useTranslation();
  const initialDateRange: Range[] = [
    {
      startDate: undefined,
      endDate: undefined,
      key: "selection",
    },
  ];
  const [isDateFilterOpen, setIsDateFilterOpen] = useState(false);

  const [dateRangeFilter, onDateRangeChanged] =
    useState<Range[]>(initialDateRange);
  const [employerSearchValue, setEmployerSearchValue] = useState("");
  const [expertSearchValue, setExpertSearchValue] = useState("");

  const statusFilters = [
    {
      id: STATUS.SCHEDULED,
      name: t("Sessions.StatusFilter.Scheduled"),
    },
    {
      id: STATUS.COMPLETED,
      name: t("Sessions.StatusFilter.Completed"),
    },
    { id: "", name: t("Sessions.StatusFilter.All") },
  ];

  const dateFilterButtonRef = useRef<HTMLButtonElement>(null);

  const onSearchChange = useDebouncedCallback((searchValue: string) => {
    const newFilters = { ...filters, searchTerm: searchValue };
    onFilterChanged(newFilters);
  }, 800);

  const { data: employers = [], isLoading: employersLoading } =
    useEmployersQuery(employerSearchValue, employerSearchValue.length >= 2);

  const { data, isLoading: expertsLoading } = usePsychologistsQuery(
    {
      search: expertSearchValue,
      page: 0,
      limit: 100,
    },
    undefined,
    expertSearchValue.length >= 3,
  );
  const experts = data?.data ?? [];

  const handleEmployerChange = (employer: ModelsEmployer | null) => {
    const newFilters = { ...filters, employer: employer ?? undefined };
    onFilterChanged(newFilters);
  };
  const handleExpertChange = (expert: ResponsePsychologistResponse | null) => {
    const newFilters = { ...filters, expert: expert ?? undefined };
    onFilterChanged(newFilters);
  };
  const handleStatusChange = (status: ModelsConsultStatus) => {
    const newFilters = { ...filters, status };
    onFilterChanged(newFilters);
  };

  const handleFilterCleared = () => {
    onDateRangeChanged(initialDateRange);
    onFilterCleared();
  };

  useEffect(() => {
    const [range] = dateRangeFilter;
    if (!range.startDate && !range.endDate) {
      return;
    }

    const newFilters = {
      ...filters,
      startedAtFrom: range.startDate ? startOfDay(range.startDate) : undefined,
      startedAtTo: range.endDate ? endOfDay(range.endDate) : undefined,
    };

    onFilterChanged(newFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRangeFilter]);

  return (
    <div className="flex flex-col gap-6 flex-column w-full mb-6">
      <div className="flex gap-4 flex-row">
        <ButtonGroup disableElevation>
          {statusFilters.map((filter) => (
            <Button
              key={filter.id}
              onClick={() =>
                handleStatusChange(filter.id as ModelsConsultStatus)
              }
              className={`border border-solid border-slate-300 py-2 px-4 body2 font-medium leading-5 tracking-[0.1px] ${
                (filters.status ?? "") === filter.id
                  ? "bg-indigo-800 text-white hover:bg-indigo-900"
                  : "bg-slate-300 bg-opacity-20 text-indigo-800 hover:bg-opacity-30"
              }`}
            >
              {filter.name}
            </Button>
          ))}
        </ButtonGroup>
        <DateFilterButton
          isOpen={isDateFilterOpen}
          onClick={() => setIsDateFilterOpen((prev) => !prev)}
          dateRangeFilter={dateRangeFilter}
          ref={dateFilterButtonRef}
        />
        {showExpertFilter && (
          <DropdownFilter
            data-testid="expert-filter"
            value={filters.expert ?? null}
            onSearchChanged={setExpertSearchValue}
            onChange={handleExpertChange}
            getOptionLabel={(option) =>
              `${option.firstName ?? ""} ${option.lastName ?? ""}`
            }
            isOptionEqualToValue={(option, value) => option.id === value.id}
            options={experts}
            loading={expertsLoading}
            noRecordFoundText="No Expert found"
            placeholder="Expert"
            inputIcon={<PsychologyOutlined />}
          />
        )}
        <DropdownFilter
          data-testid="employer-filter"
          value={filters.employer ?? null}
          onSearchChanged={setEmployerSearchValue}
          onChange={handleEmployerChange}
          getOptionLabel={(option) => option.name ?? ""}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          options={employers}
          loading={employersLoading}
          noRecordFoundText="No Employer found"
          placeholder="Employer"
          inputIcon={<WorkOutlineOutlined />}
        />
        <OpenUpButton variant="quaternary" onClick={handleFilterCleared}>
          {t("SessionsFilters.Clear")}
        </OpenUpButton>
      </div>
      <SearchBox
        onChange={onSearchChange}
        placeholder={searchPlaceholder}
        initialValue={filters.searchTerm}
        className="flex-grow"
      />
      <DateRangeDialog
        anchorEl={dateFilterButtonRef}
        open={isDateFilterOpen}
        onClose={() => setIsDateFilterOpen(false)}
        dateRangeFilter={dateRangeFilter}
        onDateRangeChanged={onDateRangeChanged}
      />
    </div>
  );
};
